// ========================================
// components/admin/AdminManagement.js - Admin Management
// ========================================
// PURPOSE: Super admin interface to manage other admins
// ACCESS: Only super_admin role
// FEATURES: List, create, edit, delete, reset password
// ========================================

import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'admin',
    password: '',
    isActive: true
  });

  // ========================================
  // FETCH ADMINS ON LOAD
  // ========================================
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/admins', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdmins(response.data.admins);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // OPEN MODAL FOR CREATE/EDIT
  // ========================================
  const openCreateModal = () => {
    setEditingAdmin(null);
    setFormData({
      email: '',
      fullName: '',
      role: 'admin',
      password: '',
      isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      fullName: admin.fullName,
      role: admin.role,
      password: '',
      isActive: admin.isActive
    });
    setShowModal(true);
  };

  // ========================================
  // HANDLE FORM SUBMIT
  // ========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const adminToken = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${adminToken}` } };
      
      if (editingAdmin) {
        // UPDATE EXISTING ADMIN
        const updateData = {
          fullName: formData.fullName,
          role: formData.role,
          isActive: formData.isActive
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`/api/admin/admins/${editingAdmin._id}`, updateData, config);
      } else {
        // CREATE NEW ADMIN
        await axios.post('/api/admin/admins', {
          email: formData.email,
          fullName: formData.fullName,
          role: formData.role,
          password: formData.password
        }, config);
      }
      
      setShowModal(false);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  // ========================================
  // DELETE ADMIN
  // ========================================
  const handleDelete = async (adminId, adminName) => {
    if (!window.confirm(`Delete admin "${adminName}"? This cannot be undone.`)) return;
    
    try {
      const adminToken = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete admin');
    }
  };

  // ========================================
  // GET ROLE BADGE STYLE
  // ========================================
  const getRoleBadge = (role) => {
    switch(role) {
      case 'super_admin': return { background: '#FFD700', color: '#1A1A1A' };
      case 'admin': return { background: '#2E7D32', color: 'white' };
      case 'viewer': return { background: '#666', color: 'white' };
      default: return { background: '#ccc', color: '#333' };
    }
  };

  if (loading) {
    return (
      <div className="admin-skeleton">
        <div className="admin-skeleton-header"></div>
        <div className="admin-skeleton-card"></div>
      </div>
    );
  }

  return (
    <div className="admin-management">
      {/* Header */}
      <div className="admin-card-header">
        <h3 className="admin-card-title">Admin Management</h3>
        <button onClick={openCreateModal} className="admin-export-btn all">
          + Add New Admin
        </button>
      </div>

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Admins Table */}
      <div className="admin-table-responsive">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin._id}>
                <td data-label="Name">{admin.fullName}</td>
                <td data-label="Email">{admin.email}</td>
                <td data-label="Role">
                  <span className="admin-plan-badge" style={getRoleBadge(admin.role)}>
                    {admin.role}
                  </span>
                </td>
                <td data-label="Status">
                  <span className={`admin-status-badge ${admin.isActive ? 'active' : ''}`}>
                    {admin.isActive ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td data-label="Last Login">
                  {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
                </td>
                <td data-label="Actions">
                  <button onClick={() => openEditModal(admin)} className="admin-view-btn" style={{ marginRight: '8px' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(admin._id, admin.fullName)} className="admin-view-btn" style={{ background: '#dc2626', color: 'white' }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title" style={{ color: '#FFD700' }}>
              {editingAdmin ? 'Edit Admin' : 'Add New Admin'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-input"
                  required
                  disabled={!!editingAdmin}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="form-input"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="admin">Admin (Limited)</option>
                  <option value="super_admin">Super Admin (Full Access)</option>
                </select>
              </div>
              
              {!editingAdmin && (
                <div className="form-group">
                  <label className="form-label">Temporary Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-input"
                    required={!editingAdmin}
                    placeholder="8+ characters"
                  />
                  <small className="form-hint">Admin must change password on first login</small>
                </div>
              )}
              
              {editingAdmin && (
                <div className="form-group">
                  <label className="form-label">New Password (optional)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-input"
                    placeholder="Leave blank to keep current password"
                  />
                </div>
              )}
              
              {editingAdmin && (
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    Account Active
                  </label>
                </div>
              )}
              
              <div className="modal-buttons" style={{ marginTop: '25px' }}>
                <button type="button" onClick={() => setShowModal(false)} className="modal-cancel">
                  Cancel
                </button>
                <button type="submit" className="modal-confirm">
                  {editingAdmin ? 'Save Changes' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;